import type { Product } from 'payments-api/model';
import {
  useGetRolesSuspense,
  usePostRoles,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';
import { Toast } from '@fragment/ui/src/components/Toast/Toast';

import { BillingToggle } from './BillingToggle';
import { CatalogProductTable } from './CatalogProductTable';
import { ALL_ROLES_VALUE } from './constants';

const ViewToggle = ({
  viewMode,
  setViewMode,
  readOnly,
}: {
  viewMode?: 'grid' | 'list';
  setViewMode?: (m: 'grid' | 'list') => void;
  readOnly?: boolean;
}) => (
  <BillingToggle
    readOnly={readOnly}
    initialActiveButton={viewMode === 'list' ? 'right' : 'left'}
    onLeftClick={() => setViewMode?.('grid')}
    onRightClick={() => setViewMode?.('list')}
    leftLabel={<Icon type="gridView" size="sm" />}
    rightLabel={<Icon type="listView" size="sm" />}
    buttonPadding="py-f0"
    buttonHeight="h-fit"
  />
);

const NewRoleModal = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (role: string) => void;
}) => {
  const [roleInput, setRoleInput] = useState('');
  const [createRoleError, setCreateRoleError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const createRoleMutation = usePostRoles(undefined, queryClient);
  const [isPending, setIsPending] = useState(false);
  const handleSubmit = useCallback(async () => {
    try {
      setIsPending(true);
      await createRoleMutation.mutateAsync({
        data: { role: roleInput.trim() },
      });
      await queryClient.invalidateQueries({ queryKey: ['/roles'] });
      onSubmit(roleInput.trim());
      setIsPending(false);
    } catch (err) {
      setIsPending(false);
      setCreateRoleError(
        err instanceof Error ? err.message : 'Failed to create role'
      );
    }
  }, [createRoleMutation, queryClient, onSubmit, roleInput]);
  return (
    <Modal
      width="sm"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      body={
        <div className="flex flex-col space-y-f4 font-sans">
          <span className="text-main text-md w-full text-left h-fit">
            {`Roles are categories of users and products like "Vendor", "Platform",
            "Customer", etc.`}
          </span>
          <ModalTextInput
            placeholder="Role name"
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
          />
        </div>
      }
      footer={
        <div className="flex flex-row justify-between">
          {createRoleError && <ErrorText>{createRoleError}</ErrorText>}
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            onCancel={onClose}
            submitButtonLabel={isPending ? 'Creating...' : 'Create'}
            disabled={isPending || !roleInput.trim()}
          />
        </div>
      }
    />
  );
};

const RolesTabs = ({
  selectedRoleName,
  setSelectedRoleName,
  rightSlot,
}: {
  selectedRoleName: string;
  setSelectedRoleName: (roleName: string) => void;
  rightSlot?: React.ReactNode;
}) => {
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const { data: rolesData } = useGetRolesSuspense();

  const roles = rolesData.data;
  const roleFilterOptions = useMemo(
    () => [
      { value: ALL_ROLES_VALUE, label: 'All' },
      ...roles
        .map((r) => ({ value: r.role, label: capitalize(r.role) }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    ],
    [roles]
  );

  const onCreateRole = useCallback(
    (roleName: string) => {
      setShowNewRoleModal(false);
      setSelectedRoleName(roleName);
    },
    [setShowNewRoleModal, setSelectedRoleName]
  );

  return (
    <>
      <div className="group flex items-end w-full">
        <TabsV2
          options={roleFilterOptions}
          selectedValue={selectedRoleName}
          onClick={setSelectedRoleName}
        />
        <button
          type="button"
          onClick={() => setShowNewRoleModal(true)}
          className="aspect-square py-f1 flex items-center justify-center text-xmd text-main-500 border-b-[0.5px] border-r-[0.5px] border-main-200 opacity-0 group-hover:opacity-100 transition-opacity hover:text-main hover:cursor-pointer"
        >
          +
        </button>
        <div className="flex-1 border-b-[0.5px] border-main-200" />
        {rightSlot}
      </div>
      {showNewRoleModal && (
        <NewRoleModal
          onClose={() => {
            setShowNewRoleModal(false);
          }}
          onSubmit={onCreateRole}
        />
      )}
    </>
  );
};

const TILE_BORDER = 'border-r-[0.5px] border-b-[0.5px] border-main-200';
const FILLER_BORDER = 'border-r-[0.5px] border-main-200';

const SKELETON_MAX_TILES = 12;

type Props = {
  workspaceId: string;
};

const CatalogPageLoading = () => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Catalog"
      primaryAction={{ title: 'New', onClick: () => {}, disabled: true }}
    >
      <div className="flex flex-col w-full min-h-0">
        <div className="sticky top-0 z-10 shrink-0">
          <div className="flex items-end w-full">
            <div className="flex-1 border-b-[0.5px] border-main-200 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
            <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-200 px-f1 self-stretch">
              <ViewToggle readOnly />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] w-full min-h-[360px]">
          <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
          {Array.from({ length: visibleTiles }).map((_, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`skeleton-${i}`}
              className={clsx('aspect-[8/9]', TILE_BORDER)}
            />
          ))}
          {Array.from({ length: 8 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`filler-${i}`} className={FILLER_BORDER} />
          ))}
        </div>
      </div>
    </PaymentViewLayout>
  );
};

const CatalogListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Catalog');

  const { showToast } = useToast();

  const [selectedRoleName, setSelectedRoleName] =
    useState<string>(ALL_ROLES_VALUE);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showDraftProduct, setShowDraftProduct] = useState(false);

  const onProductClick = useCallback(
    (_product: Product) => {
      showToast(
        <Toast type="success" message="Product detail page coming soon" />
      );
    },
    [showToast]
  );

  return (
    <PaymentViewLayout
      title="Catalog"
      primaryAction={{
        title: 'Add',
        onClick: () => setShowDraftProduct(true),
        disabled: selectedRoleName === ALL_ROLES_VALUE || showDraftProduct,
      }}
    >
      <div className="flex flex-col w-full h-full min-h-0">
        <div className="sticky top-0 z-10 shrink-0">
          <Suspense
            fallback={
              <div className="flex items-end w-full">
                <div className="flex-1 border-b-[0.5px] border-main-200 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
                <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-200 px-f1 self-stretch">
                  <ViewToggle readOnly />
                </div>
              </div>
            }
          >
            <RolesTabs
              selectedRoleName={selectedRoleName}
              setSelectedRoleName={setSelectedRoleName}
              rightSlot={
                <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-200 px-f1 self-stretch">
                  <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
                </div>
              }
            />
          </Suspense>
        </div>
        <div className="flex flex-col w-full min-h-0 flex-1">
          <CatalogProductTable
            selectedRoleName={selectedRoleName}
            onProductClick={onProductClick}
            viewMode={viewMode}
            showDraftProduct={showDraftProduct}
            onDraftClose={() => setShowDraftProduct(false)}
          />
        </div>
      </div>
    </PaymentViewLayout>
  );
};

export const CatalogPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<CatalogPageLoading />}>
    <CatalogListPageContent workspaceId={workspaceId} />
  </Suspense>
);
